import type { Account, AvatarCrop } from "./api.ts";

/** Side of the stored picture, in pixels: three times the largest it is shown. */
export const AVATAR_SIZE = 256;

/** "Lucas Goncalves", or the username until a name is given. */
export function displayName(account: Pick<Account, "username" | "firstName" | "lastName">): string {
  return [account.firstName, account.lastName].filter(Boolean).join(" ") || account.username;
}

/** What stands in for a missing picture: the name's initials, or the username's first letter. */
export function initials(account: Pick<Account, "username" | "firstName" | "lastName">): string {
  const letters = [account.firstName, account.lastName].map((part) => part?.trim().charAt(0) ?? "").join("");
  return (letters || account.username.charAt(0)).toLocaleUpperCase("fr-FR");
}

export const ROLE_LABEL: Record<Account["role"], string> = { athlete: "Athlète", coach: "Coach" };

/** The kept photo's long side: room to reframe, a few hundred kilobytes. */
export const PHOTO_MAX_SIDE = 1280;

/** An image, decoded and ready to draw. */
export async function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = src;
  await image.decode();
  return image;
}

/**
 * A photo straight off the phone is a few megabytes of HEIC: shrunk to a
 * JPEG worth keeping. An <img> rather than createImageBitmap: it applies the
 * EXIF rotation everywhere, so a portrait does not come out lying down —
 * and the JPEG drawn from it carries no rotation left to apply.
 */
export async function shrinkPhoto(file: Blob): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    const ratio = Math.min(1, PHOTO_MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.round(image.naturalWidth * ratio);
    const height = Math.round(image.naturalHeight * ratio);
    return await drawJpeg(width, height, (context) => context.drawImage(image, 0, 0, width, height));
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The chosen square of the kept photo, at the stored size. */
export function cutAvatar(photo: HTMLImageElement, crop: AvatarCrop): Promise<Blob> {
  return drawJpeg(AVATAR_SIZE, AVATAR_SIZE, (context) =>
    context.drawImage(photo, crop.x, crop.y, crop.size, crop.size, 0, 0, AVATAR_SIZE, AVATAR_SIZE),
  );
}

function drawJpeg(width: number, height: number, draw: (context: CanvasRenderingContext2D) => void): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("Impossible de préparer la photo."));
  draw(context);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Impossible de préparer la photo."))),
      "image/jpeg",
      0.85,
    ),
  );
}
